/**
 * WebhookDeliveryPage — W2.5
 *
 * Route /developer/webhooks/:webhookId/deliveries
 * Pagy-paginierte Delivery-Historie.
 * Nur-Fehler-Filter, aufklappbare Response-Body, HMAC-Hinweis.
 * Light/Dark-Mode aware, Tabelle horizontal scrollbar auf < 600px.
 */
import { useState } from 'react'
import { ArrowLeft, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useWebhookDeliveries, type WebhookDelivery } from './useWebhookDeliveries'
import { activeBcp47 } from '../../lib/locale'

// --- Formatierung ---

function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString(activeBcp47(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function truncateBody(body: string | null, max = 100): string {
  if (!body) return ''
  return body.length > max ? body.slice(0, max) + '...' : body
}

// --- Delivery-Row ---

type DeliveryRowProps = { delivery: WebhookDelivery }

function DeliveryRow({ delivery }: DeliveryRowProps) {
  const [expanded, setExpanded] = useState(false)
  const { t } = useTranslation('developer')
  const hasMore = delivery.error_message && delivery.error_message.length > 100

  return (
    <>
      <tr
        data-testid={`delivery-row-${delivery.id}`}
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        {/* Datum */}
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 13, color: 'var(--text-subtle)' }}>
          {formatDate(delivery.created_at)}
        </td>

        {/* Event-Typ */}
        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
          <span style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12, color: 'var(--text-h)' }}>
            {delivery.event_type}
          </span>
        </td>

        {/* HTTP-Status */}
        <td style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
          {delivery.http_status !== null ? (
            <span
              style={{
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'ui-monospace, Consolas, monospace',
                background: delivery.success
                  ? 'color-mix(in srgb, var(--success, #16a34a) 10%, transparent)'
                  : 'color-mix(in srgb, var(--danger, #dc2626) 10%, transparent)',
                color: delivery.success ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)',
              }}
            >
              {delivery.http_status}
            </span>
          ) : (
            <span style={{ color: 'var(--text-subtle)', fontSize: 12 }}>—</span>
          )}
        </td>

        {/* Erfolg/Fehler-Badge */}
        <td style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
          <span
            data-testid={`delivery-success-badge-${delivery.id}`}
            style={{
              padding: '2px 8px',
              borderRadius: 12,
              fontSize: 11,
              fontWeight: 600,
              background: delivery.success
                ? 'color-mix(in srgb, var(--success, #16a34a) 12%, transparent)'
                : 'color-mix(in srgb, var(--danger, #dc2626) 12%, transparent)',
              color: delivery.success ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)',
              border: delivery.success
                ? '1px solid color-mix(in srgb, var(--success, #16a34a) 25%, transparent)'
                : '1px solid color-mix(in srgb, var(--danger, #dc2626) 25%, transparent)',
            }}
          >
            {delivery.success ? t('deliveries.badges.ok') : t('deliveries.badges.error')}
          </span>
        </td>

        {/* Dauer */}
        <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap', fontSize: 13, color: 'var(--text-subtle)' }}>
          {delivery.duration_ms !== null ? `${delivery.duration_ms}ms` : '—'}
        </td>

        {/* Fehlermeldung (gekürzt + aufklappbar) — der rohe Response-Body
            des Zielsystems wird vom BE bewusst nicht ausgeliefert (SEC-BE-09). */}
        <td style={{ padding: '10px 12px', minWidth: 120, maxWidth: 260 }}>
          {delivery.error_message ? (
            <div style={{ fontSize: 12 }}>
              <span style={{ fontFamily: 'ui-monospace, Consolas, monospace', color: 'var(--text-subtle)', wordBreak: 'break-all' }}>
                {expanded ? delivery.error_message : truncateBody(delivery.error_message)}
              </span>
              {hasMore && (
                <button
                  type="button"
                  onClick={() => setExpanded(v => !v)}
                  aria-expanded={expanded}
                  data-testid={`delivery-expand-btn-${delivery.id}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                    marginLeft: 4,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 11,
                    color: 'var(--primary, #6366f1)',
                    padding: 0,
                    verticalAlign: 'middle',
                  }}
                >
                  {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  {expanded ? t('deliveries.body.less') : t('deliveries.body.more')}
                </button>
              )}
            </div>
          ) : (
            <span style={{ color: 'var(--text-subtle)', fontSize: 12 }}>—</span>
          )}
        </td>
      </tr>
    </>
  )
}

// --- HMAC-Hinweis ---

function HmacHint() {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('developer')

  return (
    <div
      style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}
      data-testid="hmac-hint"
    >
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          background: 'var(--surface, color-mix(in srgb, var(--text) 4%, transparent))',
          border: 'none',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--text-h)',
          textAlign: 'left',
        }}
        aria-expanded={open}
        data-testid="hmac-hint-toggle"
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertCircle size={14} aria-hidden="true" />
          {t('deliveries.hmacHint.toggle')}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <pre
          data-testid="hmac-hint-code"
          style={{
            margin: 0,
            padding: '12px 14px',
            overflowX: 'auto',
            fontSize: 12,
            fontFamily: 'ui-monospace, Consolas, monospace',
            background: 'color-mix(in srgb, var(--text) 4%, transparent)',
            color: 'var(--text-h)',
            borderTop: '1px solid var(--border)',
          }}
        >
{`${t('webhooks.codeExample.comment')}
const crypto = require('crypto')
const signature = request.headers['x-heliumtracker-signature']
const expected = 'sha256=' + crypto
  .createHmac('sha256', WEBHOOK_SECRET)
  .update(request.body)
  .digest('hex')
if (!crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected))) {
  throw new Error('${t('webhooks.codeExample.invalidSignature')}')
}`}
        </pre>
      )}
    </div>
  )
}

// --- Pagination ---

type PaginationProps = {
  page: number
  pages: number
  onPage: (p: number) => void
}

function Pagination({ page, pages, onPage }: PaginationProps) {
  const { t } = useTranslation('developer')

  if (pages <= 1) return null

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}
      data-testid="pagination"
    >
      <button
        className="btn btn-ghost btn-icon"
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
        aria-label={t('deliveries.pagination.prevLabel')}
        data-testid="pagination-prev"
        style={{ minWidth: 36, minHeight: 36, border: '1px solid var(--border)', borderRadius: 6 }}
      >
        <ChevronLeft size={16} aria-hidden="true" />
      </button>
      <span style={{ fontSize: 13, color: 'var(--text-subtle)' }}>
        {t('deliveries.pagination.page', { page, pages })}
      </span>
      <button
        className="btn btn-ghost btn-icon"
        onClick={() => onPage(page + 1)}
        disabled={page >= pages}
        aria-label={t('deliveries.pagination.nextLabel')}
        data-testid="pagination-next"
        style={{ minWidth: 36, minHeight: 36, border: '1px solid var(--border)', borderRadius: 6 }}
      >
        <ChevronRight size={16} aria-hidden="true" />
      </button>
    </div>
  )
}

// --- Main Component ---

type Props = {
  webhookId: number
}

export function WebhookDeliveryPage({ webhookId }: Props) {
  const [page, setPage] = useState(1)
  const [onlyFailures, setOnlyFailures] = useState(false)
  const { t } = useTranslation('developer')

  const { deliveries, meta, isLoading, isError, refetch } = useWebhookDeliveries({
    webhookId,
    page,
    onlyFailures,
  })

  function handleFilterToggle() {
    setOnlyFailures(v => !v)
    setPage(1)
  }

  return (
    <main className="webhook-delivery-page">
      {/* Back-Link */}
      <div style={{ marginBottom: 16 }}>
        <Link
          to="/developer/webhooks"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--text-subtle)' }}
          data-testid="back-to-webhooks"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          {t('deliveries.backToWebhooks')}
        </Link>
      </div>

      <header className="page-header" style={{ display: 'flex', alignItems: 'center', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, flex: 1, fontSize: 22 }}>{t('deliveries.pageTitle')}</h1>

        {/* Nur-Fehler-Filter */}
        <button
          className={`btn ${onlyFailures ? 'btn-primary' : 'btn-ghost'}`}
          onClick={handleFilterToggle}
          data-testid="filter-failures-btn"
          aria-pressed={onlyFailures}
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontWeight: 500,
            fontSize: 13,
            ...(onlyFailures
              ? { background: 'var(--danger, #dc2626)', color: '#fff', borderColor: 'var(--danger, #dc2626)' }
              : {}),
          }}
        >
          {t('deliveries.filterFailures')}
        </button>
      </header>

      {/* HMAC-Hinweis */}
      <HmacHint />

      {/* Loading */}
      {isLoading && (
        <div aria-busy="true" aria-label={t('deliveries.loading')} data-testid="loading" style={{ textAlign: 'center', padding: 32, color: 'var(--text-subtle)' }}>
          {t('deliveries.loadingText')}
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div role="alert" data-testid="error-state" style={{ textAlign: 'center', padding: 32 }}>
          <p style={{ color: 'var(--danger, #dc2626)', marginBottom: 12 }}>{t('deliveries.error')}</p>
          <button className="btn btn-primary" onClick={refetch}>{t('common:actions.retry')}</button>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && deliveries.length === 0 && (
        <div data-testid="empty-deliveries" style={{ textAlign: 'center', padding: 40, color: 'var(--text-subtle)', fontSize: 14 }}>
          {onlyFailures ? t('deliveries.emptyFailures') : t('deliveries.emptyAll')}
        </div>
      )}

      {/* Tabelle — horizontal scrollbar auf < 600px */}
      {!isLoading && !isError && deliveries.length > 0 && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table
              data-testid="deliveries-table"
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 13,
                border: '1px solid var(--border)',
                borderRadius: 8,
              }}
              aria-label={t('deliveries.tableAriaLabel')}
            >
              <thead>
                <tr style={{ background: 'color-mix(in srgb, var(--text) 4%, transparent)', borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text-subtle)', fontSize: 12 }}>{t('deliveries.columns.date')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text-subtle)', fontSize: 12 }}>{t('deliveries.columns.event')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text-subtle)', fontSize: 12 }}>{t('deliveries.columns.http')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text-subtle)', fontSize: 12 }}>{t('deliveries.columns.status')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text-subtle)', fontSize: 12 }}>{t('deliveries.columns.duration')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-subtle)', fontSize: 12 }}>{t('deliveries.columns.response')}</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((delivery: WebhookDelivery) => (
                  <DeliveryRow key={delivery.id} delivery={delivery} />
                ))}
              </tbody>
            </table>
          </div>

          {meta && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-subtle)', textAlign: 'right' }}>
              {t('deliveries.totalCount', { count: meta.count })}
            </div>
          )}

          {meta && (
            <Pagination
              page={meta.page}
              pages={meta.pages}
              onPage={setPage}
            />
          )}
        </>
      )}
    </main>
  )
}
